import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import * as MaplibreGrid from 'maplibre-grid'
import { bboxPolygon } from '@turf/bbox-polygon'
import { center } from '@turf/center'

import useDebounce from '@App/hooks/useDebounce'
import IconBtnHor from '@App/components/IconBtnHor/IconBtnHor'
import DataSectionTitle from '@App/components/DataSectionTitle/DataSectionTitle'
import LCLegendControl from '@App/components/Map/controls/LCLegendControl/LCLegendControl'
import { toggleActiveExtraLayers, setSmartTrapBufferInMeters, setGridSpacingInKilometers } from '@App/pages/MissionSetup/mission-setup.slice'

import { mapTrapFeatureToBuffer, addLandCoverLayers, removeLandCoverLayers, addRfCoverageLayers, removeRfCoverageLayers, convertBBox } from './additional-layers.utils'

import './additional-layers.styl'

function AdditionalLayers({ map }) {
  const dispatch = useDispatch()
  const { activeProject, activeExtraLayers = [], smartTrapBufferInMeters, gridSpacingInKilometers } = useSelector(state => state.missionSetup)
  const isBarcelonaMission = activeProject?.id === 'Barcelona'
  const debouncedBufferValue = useDebounce(smartTrapBufferInMeters, 500)
  const debouncedGridValue = useDebounce(gridSpacingInKilometers, 1000)
  const [sourceUpdateTrigger, setSourceUpdateTrigger] = useState(0)

  // Trap buffer layer logic
  useEffect(() => {
    if (!map) return

    const handleSourceData = (e) => {
      if ((e.sourceId === 'traps' || e.sourceId === 'planner-traps') && e.isSourceLoaded) {
        setSourceUpdateTrigger(prev => prev + 1)
      }
    }

    map.on('sourcedata', handleSourceData)
    return () => map.off('sourcedata', handleSourceData)
  }, [map])

  useEffect(() => {
    if (map) {
      const features = activeExtraLayers.includes('trap-buffer')
        ? (() => {
            const plannerTrapsSource = map.getSource('planner-traps')
            const trapsSource = map.getSource('traps')

            const plannerTrapsFeatures = plannerTrapsSource?._data?.features || []
            const trapsFeatures = trapsSource?._data?.features || []
            const allFeatures = [...plannerTrapsFeatures, ...trapsFeatures]
            return allFeatures.map(trap => mapTrapFeatureToBuffer(trap, debouncedBufferValue))
          })()
        : []

      const data = {
        type: 'FeatureCollection',
        features,
      }

      const source = map.getSource('trap-buffers')
      if (source) {
        source.setData(data)
      }
    }

    return () => {
      const source = map?.getSource('trap-buffers')
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }
  }, [map, activeExtraLayers, debouncedBufferValue, sourceUpdateTrigger])

  // Land cover layer logic
  useEffect(() => {
    const lclegendControl = new LCLegendControl(map)
    if (map && activeExtraLayers.includes('land-cover')) {
      map.addControl(lclegendControl, 'bottom-right')
      addLandCoverLayers(map, activeProject.gtifs)
    }

    return () => {
      if (map) {
        map.removeControl(lclegendControl)
        removeLandCoverLayers(map, activeProject.gtifs)
      }
    }
  }, [map, activeProject, activeExtraLayers])

  // RF coverage layer logic
  useEffect(() => {
    const activeRfCoverageLayers = activeExtraLayers.filter(layer => layer.startsWith('rf-coverage-'))

    if (map && isBarcelonaMission && activeRfCoverageLayers.length > 0) {
      addRfCoverageLayers(map, activeRfCoverageLayers)
    }

    return () => {
      if (map) {
        removeRfCoverageLayers(map)
      }
    }
  }, [map, activeExtraLayers, isBarcelonaMission])

  // Grid layer logic
  useEffect(() => {
    const [[minX, minY], , [maxX, maxY]] = activeProject.bbox.geometry.coordinates[0]
    const turfBBox = convertBBox([minY, minX, maxY, maxX])
    const polygon = bboxPolygon(turfBBox)
    const notZero = gridSpacingInKilometers === '' || gridSpacingInKilometers === '0' ? 0.2 : gridSpacingInKilometers
    const [, lat] = center(polygon).geometry.coordinates

    const gridControl = new MaplibreGrid.Grid({
      gridWidth: notZero / Math.cos(lat * Math.PI / 180),
      gridHeight: notZero,
      units: 'kilometers',
      paint: {
        'line-opacity': 0.2,
      },
    })

    if (map && activeExtraLayers.includes('grid')) {
      map.addControl(gridControl)

      requestAnimationFrame(() => {
        const layers = map.getStyle().layers
        const gridLayer = layers.find(layer => layer.id.startsWith('grid-'))
        if (gridLayer) {
          map.moveLayer(gridLayer.id, 'environment-polygon-layer')
        }
      })

      // Force grid to render by triggering a minimal pan
      const center = map.getCenter()
      map.panTo([center.lng + 0.0000001, center.lat])
    }

    return () => map && map.removeControl(gridControl)
  }, [activeProject.bbox, map, activeExtraLayers, gridSpacingInKilometers])

  useEffect(() => {
    if (gridSpacingInKilometers === debouncedGridValue) {
      if (Number(debouncedGridValue) < 0.1) {
        dispatch(setGridSpacingInKilometers(0.1))
      }
      else {
        dispatch(setGridSpacingInKilometers(debouncedGridValue))
      }
    }
  }, [dispatch, gridSpacingInKilometers, debouncedGridValue])

  return (
    <div className="additional-layers">
      <DataSectionTitle>Additional layers</DataSectionTitle>
      <div className="additional-layers__buttons">
        <IconBtnHor
          inputIncluded
          min={0}
          max={100000}
          unit="m"
          step={10}
          value={smartTrapBufferInMeters}
          iconProps={{ icon: '/img/icons/buffer.svg', size: '21px' }}
          inactive={!activeExtraLayers.includes('trap-buffer')}
          onClick={() => dispatch(toggleActiveExtraLayers('trap-buffer'))}
          onChange={(val) => {
            if (val <= 100000) dispatch(setSmartTrapBufferInMeters(val))
          }}
        >
          Buffer around traps:
        </IconBtnHor>
        <IconBtnHor
          iconProps={{ icon: '/img/icons/distance.svg', size: '21px' }}
          inactive={!activeExtraLayers.includes('distance')}
          onClick={() => dispatch(toggleActiveExtraLayers('distance'))}
        >
          Trap spacing on trap hover
        </IconBtnHor>
        <IconBtnHor
          inputIncluded
          min={0}
          max={1000}
          unit="km"
          step={0.1}
          value={gridSpacingInKilometers}
          iconProps={{ icon: '/img/icons/gridlines.svg', size: '21px' }}
          inactive={!activeExtraLayers.includes('grid')}
          onClick={() => dispatch(toggleActiveExtraLayers('grid'))}
          onChange={(val) => {
            if (val <= 100000) dispatch(setGridSpacingInKilometers(val))
          }}
        >
          Grid of
        </IconBtnHor>
        <IconBtnHor
          iconProps={{ icon: '/img/icons/satellite-imagery.svg', size: '21px' }}
          inactive={!activeExtraLayers.includes('land-cover')}
          onClick={() => dispatch(toggleActiveExtraLayers('land-cover'))}
        >
          Land cover classification
        </IconBtnHor>
        {isBarcelonaMission && (
          <>
            <IconBtnHor
              iconProps={{ icon: '/img/icons/tower-cell-regular-full.svg', size: '21px' }}
              inactive={!activeExtraLayers.includes('rf-coverage-lte-m')}
              onClick={() => dispatch(toggleActiveExtraLayers('rf-coverage-lte-m'))}
            >
              LTE-M Coverage
            </IconBtnHor>
            <IconBtnHor
              iconProps={{ icon: '/img/icons/tower-cell-regular-full.svg', size: '21px' }}
              inactive={!activeExtraLayers.includes('rf-coverage-5g-nr')}
              onClick={() => dispatch(toggleActiveExtraLayers('rf-coverage-5g-nr'))}
            >
              5G NR Coverage
            </IconBtnHor>
          </>
        )}
      </div>
    </div>
  )
}

export default AdditionalLayers
